import { useContext, useEffect, useMemo, useState } from "react";
import { IAssignedPatient, PATIENT_STATUS } from "@home-health-notify/domain";
import {
  AssignedPatientsByStatusContainer,
  FilteredPatientsListWrapper,
} from "./AssignedPatients.style";
import { SearchBox } from "../../common/SearchBox";
import { SearchBoxWrapper } from "../../message/messagePanelMenu/MessagePanel.style";
import CommonDialog from "../../common/CommonDialog/CommonDialog";
import ScrollListContainer from "../../common/ScrollListContainer";
import { Box } from "@mui/material";
import { PatientHeaderDescription } from "../../message/messageHeader/patientGroupHeader/PatientHeaderDescription";
import {
  AssignedPatientsContext,
  getAssignedPatientsByStatus,
  OrganizationContext,
} from "hhn-commons";
import { GROUP_AVATAR_SIZE } from "../../utils/AvatarUtils";
import EmptyRecordView from "../../common/EmptyRecordView";
import { ROUTES } from "../../routes";
import { useNavigate } from "react-router";

interface AssignedPatientsByStatusModalProps {
  closeModal: () => void;
  isModalOpen: boolean;
  patientStatus: PATIENT_STATUS;
}

export const AssignedPatientsByStatusModal = ({
  closeModal,
  isModalOpen,
  patientStatus,
}: AssignedPatientsByStatusModalProps) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { assignedPatients } = useContext(AssignedPatientsContext);
  const navigate = useNavigate();

  const patients = useMemo(
    () => getAssignedPatientsByStatus(assignedPatients, patientStatus),
    [assignedPatients, patientStatus],
  );

  const [search, setSearch] = useState<string>("");
  const [filteredPatients, setFilteredPatients] =
    useState<IAssignedPatient[]>(patients);

  useEffect(() => {
    setFilteredPatients(patients);
  }, [patients]);

  const onSearchClear = () => {
    setSearch("");
    setFilteredPatients(patients);
  };

  const onSearchChange = (searchQuery: string) => {
    setSearch(searchQuery);
    const lowerCaseSearch = searchQuery.toLowerCase();
    const filtered = patients.filter(patient =>
      patient.name.toLowerCase().includes(lowerCaseSearch),
    );
    setFilteredPatients(filtered);
  };

  const goToChat = (patient: IAssignedPatient) => {
    const orgId = selectedOrgUser?.getOrganization().organizationId;
    navigate(
      `${ROUTES.ORGANIZATIONS}/${orgId}/${ROUTES.CHATS}/${ROUTES.PATIENT}/${patient.id}/${ROUTES.MESSAGE}`,
    );
  };

  return (
    <CommonDialog
      isDialogOpen={isModalOpen}
      onDialogClose={closeModal}
      title={"Patients List"}
      titleAlignment="center"
    >
      <AssignedPatientsByStatusContainer>
        <SearchBoxWrapper>
          <SearchBox
            placeholderLabel={"Search patients..."}
            searchText={search}
            onSearchTextChange={onSearchChange}
            onSearchClear={onSearchClear}
            isLoading={false}
          />
        </SearchBoxWrapper>
        <ScrollListContainer
          dataTestId="assigned-patients-scrollable-container"
          restTop={200}
        >
          <FilteredPatientsListWrapper>
            {filteredPatients.length > 0 ? (
              <>
                {filteredPatients
                  .toSorted((a, b) => a.name.localeCompare(b.name))
                  .map((patient: IAssignedPatient) => (
                  <Box
                    style={{ cursor: "pointer" }}
                    key={patient.id}
                    onClick={() => goToChat(patient)}
                    data-testid="assigned-patient-list-item"
                  >
                    <PatientHeaderDescription
                      name={patient.name}
                      patientStatus={Number(patient.patientStatus)}
                      servicesToPatient={patient.servicesToPatient}
                      avatarSize={GROUP_AVATAR_SIZE.SMALL}
                      fontSizeTitle={20}
                    />
                  </Box>
                ))}
              </>
            ) : (
              <EmptyRecordView emptyMessage="No matching patients found" />
            )}
          </FilteredPatientsListWrapper>
        </ScrollListContainer>
      </AssignedPatientsByStatusContainer>
    </CommonDialog>
  );
};
